import {
  baseUrl,
  REACT_APP_COOKIE_DOMAIN,
  REACT_APP_COOKIE_SECURE,
} from "../connections";

const getPhoneValue = (mobile) => String(mobile || "").replace("+", "");

const getPhoneParts = (mobile) => {
  const phoneValue = getPhoneValue(mobile);

  if (!phoneValue) {
    return {
      code: "1",
      value: "",
    };
  }

  if (phoneValue.length <= 10) {
    return {
      code: "1",
      value: phoneValue,
    };
  }

  return phoneValue.length === 11
    ? {
        code: phoneValue.slice(0, 1),
        value: phoneValue.slice(1),
      }
    : {
        code: phoneValue.slice(0, 2),
        value: phoneValue.slice(2),
      };
};

const decodeBase64Url = (value) => {
  const normalized = String(value || "")
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");

  return decodeURIComponent(
    atob(padded)
      .split("")
      .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`)
      .join(""),
  );
};

const encodeBase64Url = (value) =>
  btoa(
    encodeURIComponent(String(value || "")).replace(
      /%([0-9A-F]{2})/g,
      (_, hex) => String.fromCharCode(Number.parseInt(hex, 16)),
    ),
  )
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");

export const parseOAuthProfile = (oauthProfile) => {
  if (!oauthProfile) {
    return null;
  }

  return JSON.parse(decodeBase64Url(oauthProfile));
};

export const serializeOAuthProfile = (profile) =>
  encodeBase64Url(JSON.stringify(profile || {}));

export const normalizeOAuthProfile = (profile = {}) => {
  const safeProfile = profile || {};

  return {
    ...safeProfile,
    firstName:
      safeProfile?.firstName ||
      safeProfile?.given_name ||
      safeProfile?.givenName ||
      "",
    lastName:
      safeProfile?.lastName ||
      safeProfile?.family_name ||
      safeProfile?.familyName ||
      "",
    email: safeProfile?.email || "",
  };
};

export const hasOAuthSession = (profile = {}) =>
  Boolean(profile?.token || profile?.idToken || profile?.isAuthenticated);

export const extractAuthData = (payload = {}) => ({
  ...(payload?.data || {}),
  ...(payload?.bingoDetail || {}),
  token:
    payload?.token || payload?.data?.token || payload?.bingoDetail?.token || "",
});

export const buildUserSession = (data = {}) => {
  const safeData = data || {};
  const phone = getPhoneParts(safeData?.mobile || safeData?.phone?.value);

  return {
    ...safeData,
    firstName: safeData?.firstName || "",
    lastName: safeData?.lastName || "",
    avatar: safeData?.avatar || safeData?.profile_pic || "",
    mobile: safeData?.mobile || "",
    ssn: safeData?.ssn || "",
    id_proof: safeData?.id_proof || "",
    id_number: safeData?.id_number || "",
    gender: safeData?.gender || "",
    age: safeData?.age || "",
    intrests: safeData?.interests || safeData?.intrests || [],
    languages: safeData?.languages || [],
    religion: safeData?.religion || "",
    phone,
    primaryEmail: safeData?.email || safeData?.primaryEmail || "",
    address: {
      ...(safeData?.address || {}),
      location: safeData?.address1 || safeData?.address?.location || "",
      city: safeData?.city || safeData?.address?.city || "",
      addressLine1: safeData?.address2 || safeData?.address?.addressLine1 || "",
      state: safeData?.state || safeData?.address?.state || "",
      county: safeData?.county || safeData?.address?.county || "",
      country: safeData?.country || safeData?.address?.country || "",
      apartmentNo:
        safeData?.apartment_number || safeData?.address?.apartmentNo || "",
      zip: safeData?.pincode || safeData?.address?.zip || "",
      landmark: safeData?.landmark || safeData?.address?.landmark || "",
    },
    demographics: {
      ...(safeData?.demographics || {}),
      race: safeData?.ethnicity || safeData?.demographics?.race || "",
      dob: safeData?.dob || safeData?.demographics?.dob || "",
    },
    location: data?.location || null,
    user_uuid: safeData?.uuid || safeData?.user_uuid || "",
    uuid: safeData?.uuid || safeData?.user_uuid || "",
    isAuthenticated: true,
    token: safeData?.token || "",
    rewards: safeData?.rewards,
    user_verified: safeData?.user_verified,
    user_verified_by_admin: safeData?.user_verified_by_admin,
    is_admin_sent_mail: safeData?.is_admin_sent_mail,
    dInfoUserEmail: safeData?.dInfoUserEmail,
    dInfoUserId: safeData?.dInfoUserId,
    dInfoUserType: safeData?.dInfoUserType,
    profileCompleted: safeData?.profileCompleted,
    requiresProfileCompletion: safeData?.requiresProfileCompletion,
    redirectTo: safeData?.redirectTo || "",
  };
};

export const persistUserSession = ({ data, setUserDetails, setCookie }) => {
  const userData = buildUserSession(data);

  if (typeof setUserDetails === "function") {
    setUserDetails(userData);
  }

  localStorage.setItem("auth", JSON.stringify(true));
  localStorage.setItem("user", JSON.stringify(userData));
  localStorage.setItem("token", JSON.stringify(data?.token || ""));

  if (typeof setCookie === "function") {
    const cookieOptions = {
      path: "/",
      secure: REACT_APP_COOKIE_SECURE,
      domain: REACT_APP_COOKIE_DOMAIN,
      maxAge: 30 * 24 * 60 * 60, // 30 days
    };

    [
      ["dInfoUserEmail", data?.dInfoUserEmail],
      ["dInfoUserId", data?.dInfoUserId],
      ["dInfoUserType", data?.dInfoUserType],
      ["dInfoToken", data?.token],
    ].forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        setCookie(key, value, cookieOptions);
      }
    });
  }

  return userData;
};

export const getPostAuthRoute = (data = {}) => {
  const redirectTarget = String(data?.redirectTo || "")
    .trim()
    .toLowerCase();

  if (redirectTarget === "home") {
    return "/home";
  }

  if (
    redirectTarget === "complete-profile" ||
    redirectTarget === "complete_profile"
  ) {
    return "/complete-profile";
  }

  if (data?.requiresProfileCompletion === true) {
    return "/complete-profile";
  }

  if (data?.profileCompleted === true) {
    return "/home";
  }

  if (isProfileCompletionPending(data)) {
    return "/complete-profile";
  }

  if (data?.user_verified_by_admin === false) {
    return "/check-email";
  }

  if (data?.is_admin_sent_mail === false) {
    return "/check-email";
  }

  return data?.user_verified ? "/home" : "/verification";
};

export const isProfileCompletionPending = (data = {}) => {
  if (data?.requiresProfileCompletion === true) {
    return true;
  }

  if (data?.profileCompleted === true) {
    return false;
  }

  const redirectTarget = String(data?.redirectTo || "")
    .trim()
    .toLowerCase();

  if (redirectTarget === "home") {
    return false;
  }

  if (
    redirectTarget === "complete-profile" ||
    redirectTarget === "complete_profile"
  ) {
    return true;
  }

  const firstName = String(data?.firstName || "").trim();
  const lastName = String(data?.lastName || "").trim();
  const email = String(data?.email || "").trim();
  const country = String(data?.country || data?.address?.country || "").trim();
  const state = String(data?.state || data?.address?.state || "").trim();
  const county = String(data?.county || data?.address?.county || "").trim();
  const city = String(data?.city || data?.address?.city || "").trim();
  const pincode = String(data?.pincode || data?.address?.zip || "").trim();
  const addressLine = String(
    data?.address1 || data?.address?.location || "",
  ).trim();

  return !(
    firstName &&
    lastName &&
    email &&
    country &&
    state &&
    county &&
    city &&
    /^\d{5}$/.test(pincode) &&
    addressLine
  );
};

export const getStoredAuthToken = () => {
  try {
    const storedToken = JSON.parse(localStorage.getItem("token") || '""');
    if (storedToken) {
      return storedToken;
    }
  } catch (_error) {
    // Ignore malformed local storage values and fall back to the user session.
  }

  try {
    const storedUser = JSON.parse(localStorage.getItem("user") || "null");
    return storedUser?.token || "";
  } catch (_error) {
    return "";
  }
};

export const appendAuthToken = (headers, token = "") => {
  const authToken = token || getStoredAuthToken();

  if (authToken) {
    headers.append("x-auth-token", authToken);
  }

  return headers;
};

export const fetchAuthenticatedUserProfile = async (authData = {}) => {
  const token = String(authData?.token || "").trim();
  const userUuid = String(authData?.uuid || authData?.user_uuid || "").trim();

  if (!token || !userUuid) {
    return authData;
  }

  const headers = new Headers();
  headers.append("Content-Type", "application/json");
  appendAuthToken(headers, token);

  const response = await fetch(
    `${baseUrl}/user/consumer/get/profile/${userUuid}`,
    {
      method: "GET",
      headers,
      redirect: "follow",
    },
  );
  const responseText = await response.text();

  if (!response.ok) {
    let message =
      responseText || "Authenticated user profile could not be loaded.";

    try {
      const payload = responseText ? JSON.parse(responseText) : null;
      message = payload?.message || payload?.error || message;
    } catch (_error) {
      // Ignore non-JSON profile responses and surface the raw text instead.
    }

    throw new Error(message);
  }

  let payload = {};

  try {
    payload = responseText ? JSON.parse(responseText) : {};
  } catch (_error) {
    payload = {};
  }

  return {
    ...authData,
    ...(payload?.data || {}),
    token: payload?.token || token,
  };
};
